import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

const typeMap = { 1: '借用', 2: '变更', 3: '处置', 4: '处置撤回' };
const typeColorMap = {
  1: { bg: '#e6f4ff', color: '#1677ff' },
  2: { bg: '#fff7e6', color: '#fa8c16' },
  3: { bg: '#fff2f0', color: '#ff4d4f' },
};
const statusStyle = {
  0: { bg: '#fff7e6', color: '#fa8c16', text: '待审批' },
  1: { bg: '#f6ffed', color: '#52c41a', text: '已通过' },
  2: { bg: '#fff2f0', color: '#ff4d4f', text: '已驳回' },
};

const tabItems = [
  { key: '', label: '全部' },
  { key: '1', label: '借用' },
  { key: '2', label: '变更' },
  { key: '3', label: '处置' },
  { key: '4', label: '处置撤回' },
];

export default function MyApplies() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const fetchList = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: 10 };
    if (type) params.apply_type = type;
    api.get('/applies', { params })
      .then((res) => {
        const rows = Array.isArray(res?.items) ? res.items : [];
        setList(prev => (page === 1 ? rows : [...prev, ...rows]));
        setTotal(res?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, type]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const hasMore = list.length < total;

  const getReviewText = (item) => {
    if (item.status === 0) return '待审核';
    const approver = item?.approver?.username || '-';
    return item.status === 1
      ? `已被 ${approver} 审核通过`
      : `已被 ${approver} 审核驳回`;
  };

  const firstAsset = current?.assets?.[0] || null;
  const oldUserName = firstAsset?.old_user_name || current?.old_user_name || '-';
  const newUserName = firstAsset?.new_user_name || current?.new_user_name || '-';
  const oldLocationName = firstAsset?.old_location_name || current?.old_location_name || '-';
  const newLocationName = firstAsset?.new_location_name || current?.new_location_name || '-';

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', paddingBottom: 70 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
          <span style={{ fontSize: 17, fontWeight: 600 }}>我的申请</span>
        </div>
        <div />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '0 8px' }}>
        {tabItems.map(tab => (
          <div
            key={tab.key}
            onClick={() => { setType(tab.key); setPage(1); }}
            style={{
              flex: 1,
              textAlign: 'center',
              padding: '12px 0',
              fontSize: 14,
              fontWeight: type === tab.key ? 600 : 400,
              color: type === tab.key ? '#1677ff' : '#666',
              borderBottom: type === tab.key ? '2px solid #1677ff' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* List */}
      <div style={{ padding: '12px 0' }}>
        {loading && list.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>加载中...</div>
        ) : list.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 14 }}>暂无申请记录</div>
        ) : (
          list.map(item => {
            const st = statusStyle[item.status] || {};
            const tc = typeColorMap[item.apply_type] || { bg: '#f5f5f5', color: '#999' };
            return (
              <div
                key={item.id}
                onClick={() => { setCurrent(item); setDetailOpen(true); }}
                style={{ background: '#fff', borderRadius: 12, padding: 16, margin: '0 16px 12px', cursor: 'pointer' }}
              >
                {/* Top row: review text + status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>{getReviewText(item)}</span>
                  <span style={{
                    fontSize: 12, padding: '2px 10px', borderRadius: 4,
                    background: st.bg, color: st.color,
                  }}>
                    {st.text || '-'}
                  </span>
                </div>

                {/* Type tag */}
                <div style={{ marginBottom: 8 }}>
                  <span style={{
                    fontSize: 12, padding: '2px 8px', borderRadius: 4,
                    background: tc.bg, color: tc.color,
                  }}>
                    {typeMap[item.apply_type] || '-'}
                  </span>
                </div>

                {/* Time */}
                <div style={{ fontSize: 13, color: '#999', marginBottom: 4 }}>
                  {dayjs(item.create_time).format('YYYY-MM-DD HH:mm')}
                </div>
              </div>
            );
          })
        )}

        {/* Load More */}
        {hasMore && !loading && (
          <div
            onClick={() => setPage(p => p + 1)}
            style={{ textAlign: 'center', padding: '12px 0', color: '#1677ff', fontSize: 14, cursor: 'pointer' }}
          >
            加载更多
          </div>
        )}
        {loading && list.length > 0 && (
          <div style={{ textAlign: 'center', padding: '12px 0', color: '#999', fontSize: 13 }}>加载中...</div>
        )}
      </div>

      <Modal
        title="申请详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        destroyOnClose
      >
        {current && (
          <div style={{ lineHeight: 1.9, color: '#333' }}>
            <div><b>申请类型：</b>{typeMap[current.apply_type] || '-'}</div>
            <div><b>审核结果：</b>{getReviewText(current)}</div>
            <div><b>原使用人：</b>{oldUserName}</div>
            <div><b>原存放地点：</b>{oldLocationName}</div>
            <div><b>现使用人：</b>{newUserName}</div>
            <div><b>现存放地点：</b>{newLocationName}</div>
            <div><b>审核人：</b>{current?.approver?.username || '-'}</div>
            <div><b>审核时间：</b>{current?.approve_time ? dayjs(current.approve_time).format('YYYY年MM月DD日 HH:mm') : '-'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
}
